import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import type { FC } from "react"
import Link from "@/common/components/Link"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import { useDevice } from "@/common/hooks/useDevice"
import { stakeHeaderSvg } from "@/governance/components/stake/StakeHeaderSvg"
import { BuildingColumnsRegular } from "@/ui/components/icons/font-awesome/BuildingColumnsRegular"
import { GearRegular } from "@/ui/components/icons/font-awesome/GearRegular"
import { HandHoldingDollarIcon } from "@/ui/components/icons/font-awesome/HandHoldingDollarIcon"
import { NetworkWiredIcon } from "@/ui/components/icons/font-awesome/NetworkWired"
import { ShieldCheckRegular } from "@/ui/components/icons/font-awesome/ShieldCheckRegular"

const UpsellLayout: FC<{ organizationName: string }> = ({
  organizationName,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  return (
    <Box maxW="724px" mx="auto" pt={{ md: 6 }}>
      <VStack
        backgroundColor="white"
        py={{ base: 10, md: 10 }}
        spacing={{ base: 8, md: 8 }}
        w="full"
      >
        <Flex
          alignItems="center"
          backgroundImage={encodeSvgToBase64(stakeHeaderSvg)}
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          h="74px"
          w="100%"
        />
        <Stack
          alignItems="center"
          pb={{ base: 12, md: 0 }}
          spacing={{ base: 4, md: 4 }}
          w="full"
        >
          <Text
            color="gray.900"
            fontFamily="'Hedvig Letters Serif', serif"
            fontSize={{ base: "36px", md: "40px" }}
            fontWeight="medium"
            letterSpacing="-0.5px"
            lineHeight={{ base: "44px", md: "48px" }}
            textAlign="center"
          >
            Enable staking for {organizationName}
          </Text>
          <Text
            color="gray.500"
            fontSize="16px"
            fontWeight="normal"
            letterSpacing="0px"
            lineHeight="24px"
            textAlign="center"
          >
            {organizationName} isn&apos;t using Tally staking yet. Set up
            staking to align holders and pave the way to sustainable token
            economics.
          </Text>
          <Stack direction="row" display={onLittleDevice} spacing={3}>
            <Button
              isExternal
              aria-label="Deploy staking"
              as={Link}
              fontSize="16px"
              href={EXTERNAL_ROUTES.tally.contact(
                "utm_source=tally-dao-staking&utm_medium=upsell-button",
              )}
              size="lg"
              style={{ textDecoration: "none" }}
              tabIndex={0}
              variant="primary"
              w="fit-content"
            >
              Deploy staking
            </Button>
            <Button
              isExternal
              aria-label="Learn more"
              as={Link}
              fontSize="16px"
              href={EXTERNAL_ROUTES.tally.docs.staking()}
              size="lg"
              style={{ textDecoration: "none" }}
              tabIndex={0}
              variant="outline"
            >
              Learn more
            </Button>
          </Stack>
        </Stack>

        <Stack alignItems="center" spacing={{ base: 4, md: 4 }} w="full">
          <Text
            color="gray.900"
            fontSize={{ base: "18px", md: "24px" }}
            fontWeight="bold"
            textAlign="center"
          >
            Why enable Tally Staking
          </Text>
          <Flex justifyContent="space-between" w="full" wrap="wrap">
            {cardsContent.map((content, idx) => (
              <Box
                key={content.title}
                padding={{ base: 1, md: 2 }}
                width={{ base: "100%", lg: idx > 2 ? "50%" : `${100 / 3}%` }}
              >
                <Stack
                  key={content.title}
                  alignItems="flex-start"
                  bg="white"
                  borderRadius="lg"
                  borderWidth="1px"
                  boxShadow="sm"
                  flexDirection={{ base: "row", lg: "column" }}
                  h="full"
                  p={4}
                  spacing={3}
                  width="full"
                >
                  <Icon
                    as={content.icon}
                    backgroundColor="white"
                    color="gray.500"
                    fill="gray.500"
                    h="20px"
                    w="20px"
                  />
                  <Stack spacing={1}>
                    <Text color="gray.900" fontSize="14px" fontWeight="500">
                      {content.title}
                    </Text>
                    <Text color="gray.500" fontSize="14px" fontWeight="400">
                      {content.description}
                    </Text>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Flex>
        </Stack>

        <HStack display={onLargeDevice} spacing={3}>
          <Button
            isExternal
            aria-label="Deploy staking"
            as={Link}
            fontSize="16px"
            href={EXTERNAL_ROUTES.tally.contact(
              "utm_source=tally-dao-staking&utm_medium=upsell-button",
            )}
            size="lg"
            style={{ textDecoration: "none" }}
            tabIndex={0}
            variant="primary"
          >
            Deploy staking
          </Button>
          <Button
            isExternal
            aria-label="Learn more"
            as={Link}
            fontSize="16px"
            href={EXTERNAL_ROUTES.tally.docs.staking()}
            size="lg"
            style={{ textDecoration: "none" }}
            tabIndex={0}
            variant="outline"
          >
            Learn more
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

const cardsContent = [
  {
    icon: NetworkWiredIcon,
    title: "Flexible Infrastructure",
    description: "Tailor staking to your protocol's needs",
  },
  {
    icon: HandHoldingDollarIcon,
    title: "Multiple Reward Sources",
    description:
      "Distribute rewards via protocol revenue, treasury, emissions, and more",
  },
  {
    icon: BuildingColumnsRegular,
    title: "Governance Integration",
    description: "Stake without sacrificing governance",
  },
  {
    icon: ShieldCheckRegular,
    title: "Validator Support",
    description: "Incentivize network security",
  },
  {
    icon: GearRegular,
    title: "Incentive Mechanics",
    description: "Set parameters to reward onchain actions",
  },
]

const encodeSvgToBase64 = (svg: string) => {
  if (typeof window !== "undefined") {
    const base64Svg = window.btoa(decodeURIComponent(encodeURIComponent(svg)))

    return `data:image/svg+xml;base64,${base64Svg}`
  } else {
    return `data:image/svg+xml;base64,${Buffer.from(svg).toString("base64")}`
  }
}

export default UpsellLayout
